import * as React from 'react';
import {createViewConfig, view} from '@sail/engine';
import {ButtonConfig} from '../Button';
import {tokens} from '../../tokens';
import {useUiConfig} from '../../providers/UiConfigProvider';
import {css} from '../../css';

export type DialogFooterProps = {
  /** The slot into which actions should be rendered. */
  actions: React.ReactNode;
  /** Content to be rendered on the leading side of the footer, opposite the actions. */
  leading?: React.ReactNode;
  /** Additional arbitrary content to be included before the leading/actions row. */
  children?: React.ReactNode;
};

export const DialogFooterConfig = createViewConfig({
  name: 'DialogFooter',
  props: {} as DialogFooterProps,
  flattens: true,
});

/**
 * Dialog footers contain actions and, like the header, are always visible because they do not scroll with the content. They may optionally contain other content in addition to actions.
 */
export const DialogFooter = DialogFooterConfig.createView(
  ({leading, actions, children, ...props}) => {
    const isSmallScreen = useUiConfig()?.isSmallScreen;

    return (
      <view.footer
        {...props}
        css={{
          height: 'fit',
          stack: 'y',
          ...(isSmallScreen
            ? {
                gap: 'medium',
                paddingX: tokens.space[250],
                paddingTop: 'medium',
                paddingBottom: tokens.space[250],
              }
            : {padding: 'medium'}),
        }}
      >
        {children}
        <view.div
          uses={
            isSmallScreen
              ? [
                  ButtonConfig.customize({
                    defaults: {size: 'large'},
                    css: {width: 'fill'},
                  }),
                  css({
                    stack: 'y',
                    alignX: 'stretch',
                    alignY: 'top',
                    gap: 'medium',
                  }),
                ]
              : [
                  css({
                    stack: 'x',
                    alignX: 'end',
                    alignY: 'center',
                    gap: 'small',
                  }),
                ]
          }
        >
          {leading ? (
            <view.div
              css={{
                color: 'subdued',
                ...(isSmallScreen
                  ? {font: 'label.medium'}
                  : {font: 'label.small'}),
                marginInlineEnd: 'auto',
              }}
            >
              {leading}
            </view.div>
          ) : null}
          {actions}
        </view.div>
      </view.footer>
    );
  },
);
